/**
 * 분석 대시보드 3-col 사이드바의 접기·펼치기 조작부 (2026-08-28).
 *
 * 왜 접는가 — 「AI 동정 후보」 표는 자기 컨테이너가 600px 이 안 되면 6열 중
 * 3열(ID·관찰 #·물성)을 숨긴다. 펼침 상태로 그 폭을 얻으려면 뷰포트 1700px 이
 * 필요해서, 1440 노트북과 1536(물리 1920 + Windows 125%)에서는 늘 접혀 있었다.
 * 좌·우를 모두 접으면 1280 부터 3열이 펴진다.
 * 근거: docs/2026/260828_인수인계_사이드바접기_검토.md §3 · `utils/dashboardLayout.ts`.
 *
 * **조작부의 크기를 일부러 다르게 준다.** 접기는 정보를 감추는 동작이라 실수로
 * 눌리면 손해고, 펼치기는 되돌리는 동작이라 실수로 눌려도 손해가 없다. 그래서
 * 접기는 패널 **아래쪽 끝의 작은 글자 버튼**(스티키 영역 상단처럼 스크롤 내내
 * 커서 밑에 있지 않다), 펼치기는 **레일 전체**가 버튼이다. 인수인계 문서 §6-5.
 */

type SidebarSide = "left" | "right";

interface SidebarRailProps {
  /** 패널 이름. 접힌 레일에 세로로 적힌다. */
  label: string;
  side: SidebarSide;
  /** 펼쳤을 때 나타나는 패널의 id (aria-controls). */
  controls: string;
  onExpand: () => void;
  /** 페이지의 카드 래퍼 클래스 — 레일도 같은 카드로 보이게 한다. */
  cardClassName: string;
}

/** 접힌 상태의 레일. 어디를 눌러도 펼쳐진다. */
export function SidebarRail({
  label,
  side,
  controls,
  onExpand,
  cardClassName,
}: SidebarRailProps) {
  return (
    <button
      type="button"
      onClick={onExpand}
      aria-expanded={false}
      aria-controls={controls}
      aria-label={`${label} 펼치기`}
      title={`${label} 펼치기`}
      data-testid={`sidebar-rail-${side}`}
      className={`${cardClassName} w-full flex items-center justify-center gap-2 py-3 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 md:flex-col md:py-4`}
    >
      <span aria-hidden="true" className="text-sm">
        {side === "left" ? "›" : "‹"}
      </span>
      {/* md 이상에서는 레일이 48px 이라 글자를 세로로 세운다.
          `text-orientation:upright` 이 반드시 함께 가야 한다 — 기본값(`mixed`)은
          **한글만 세우고 라틴 문자는 90° 눕힌다.** 그래서 "AI 분석 요약" 의 AI 만
          옆으로 누워 있었다(2026-08-28 배포분에서 운영자 지적). 약어·단위가 섞인
          한국어 라벨을 세로로 세울 때 늘 걸리는 함정이다. */}
      <span className="text-xs whitespace-nowrap md:[writing-mode:vertical-rl] md:[text-orientation:upright]">
        {label}
      </span>
    </button>
  );
}

interface SidebarCollapseButtonProps {
  label: string;
  side: SidebarSide;
  /** 접을 패널의 id (aria-controls). */
  controls: string;
  onCollapse: () => void;
}

/** 펼친 패널 아래쪽의 작은 접기 버튼. */
export function SidebarCollapseButton({
  label,
  side,
  controls,
  onCollapse,
}: SidebarCollapseButtonProps) {
  return (
    // pr-1.5 pb-1.5: 펼침 상태의 aside 는 `overflow-y-auto` 라 사방이 잘리는
    // 스크롤 컨테이너다. 전역 포커스 링이 요소 **바깥** 4px(offset 2 + 굵기 2)에
    // 그려지므로, 버튼을 카드 모서리에서 6px 안으로 들여야 링이 잘리지 않는다.
    // (실측: 들여쓰기 전에는 링의 오른쪽이 통째로 잘렸다.)
    <div className="flex justify-end pr-1.5 pb-1.5">
      <button
        type="button"
        onClick={onCollapse}
        aria-expanded={true}
        aria-controls={controls}
        aria-label={`${label} 접기`}
        title={`${label} 접기`}
        data-testid={`sidebar-collapse-${side}`}
        // text-gray-500: gray-400 은 흰 배경 대비 2.60:1 로 WCAG 1.4.3(4.5:1)에
        // 못 미친다. 12px 글자라 '큰 글자' 예외(3:1)도 안 된다. gray-500 은 4.84:1.
        // 같은 파일의 레일도 gray-500 을 쓴다.
        className="flex items-center gap-1 px-3 pb-2.5 pt-1 text-xs text-gray-500 transition-colors hover:text-gray-700"
      >
        <span aria-hidden="true">{side === "left" ? "‹" : "›"}</span>
        접기
      </button>
    </div>
  );
}
